// frontend/src/pages/SeasonsPage.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ChevronRight, Loader } from 'lucide-react';
import SeasonCard from '../components/SeasonCard';
import { moviesAPI } from '../services/api';

interface SeasonStats {
  winter: number;
  spring: number;
  summer: number;
  autumn: number;
}

interface HolidayStats {
  christmas: number;
  valentine: number;
  new_year: number;
}

const seasonCollections = [
  {
    season: 'winter' as const,
    title: 'Winter Warmers',
    description: 'Cozy up with these winter romance classics',
  },
  {
    season: 'spring' as const,
    title: 'Spring Blossoms',
    description: 'Fresh starts and new beginnings',
  },
  {
    season: 'summer' as const,
    title: "Summer Lovin'",
    description: 'Sunny days and warm nights',
  },
  {
    season: 'autumn' as const,
    title: 'Autumn Romance',
    description: 'Fall in love all over again',
  },
];

const holidayCollections = [
  {
    holiday: 'christmas',
    title: 'Christmas Romcoms',
    description: 'Feel-good holiday movies to get you in the spirit',
    icon: '🎄',
    color: 'bg-red-500',
  },
  {
    holiday: 'valentine',
    title: 'Valentine Specials',
    description: 'All about love and romance',
    icon: '💝',
    color: 'bg-pink-500',
  },
  {
    holiday: 'new_year',
    title: 'New Year',
    description: 'Fresh starts and midnight kisses',
    icon: '🎆',
    color: 'bg-blue-500',
  },
];

const SeasonsPage: React.FC = () => {
  const navigate = useNavigate();
  const [seasonStats, setSeasonStats] = useState<SeasonStats>({
    winter: 0, spring: 0, summer: 0, autumn: 0,
  });
  const [holidayStats, setHolidayStats] = useState<HolidayStats>({
    christmas: 0, valentine: 0, new_year: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setIsLoading(true);

        // Fetch counts for all seasons and holidays in parallel
        const [winter, spring, summer, autumn, christmas, valentine, newYear] = await Promise.all([
          moviesAPI.getBySeason('winter', 1),
          moviesAPI.getBySeason('spring', 1),
          moviesAPI.getBySeason('summer', 1),
          moviesAPI.getBySeason('autumn', 1),
          moviesAPI.getByHoliday('christmas', 1),
          moviesAPI.getByHoliday('valentine', 1),
          moviesAPI.getByHoliday('new_year', 1),
        ]);

        setSeasonStats({
          winter: winter.data.data.pagination.total,
          spring: spring.data.data.pagination.total,
          summer: summer.data.data.pagination.total,
          autumn: autumn.data.data.pagination.total,
        });

        setHolidayStats({
          christmas: christmas.data.data.pagination.total,
          valentine: valentine.data.data.pagination.total,
          new_year: newYear.data.data.pagination.total,
        });
      } catch (err) {
        console.error('Failed to fetch season stats:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, []);

  const getHolidayCount = (holiday: string): number => {
    return holidayStats[holiday as keyof HolidayStats] || 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">
      <div className="container mx-auto px-4 pt-24 pb-16">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <Calendar className="w-16 h-16 text-romcom-red mx-auto mb-4" />
          <h1 className="text-5xl md:text-6xl font-playfair font-bold text-gray-800 mb-4">
            Seasonal Collections
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover romcoms perfectly matched to each season's unique vibe and mood.
            Whether you're cozying up in winter or soaking up summer sun, we've got the perfect movie.
          </p>
        </motion.div>

        {/* Season Cards */}
        <div className="mb-16">
          <h2 className="text-3xl font-playfair font-bold text-gray-800 mb-8">
            Browse by Season
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {seasonCollections.map((collection, index) => (
              <motion.div
                key={collection.season}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <SeasonCard
                  season={collection.season}
                  title={collection.title}
                  description={collection.description}
                  movieCount={
                    isLoading ? 0 : seasonStats[collection.season]
                  }
                  onClick={() => navigate(`/seasons/${collection.season}`)}
                />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Holiday Collections */}
        <div className="mb-16">
          <h2 className="text-3xl font-playfair font-bold text-gray-800 mb-8">
            Holiday Specials
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {holidayCollections.map((holiday, index) => (
              <motion.div
                key={holiday.holiday}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
                onClick={() => navigate(`/movies?holiday=${holiday.holiday}`)}
                className={`${holiday.color} rounded-2xl p-8 text-white cursor-pointer`}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-4xl">{holiday.icon}</span>
                  <ChevronRight className="w-6 h-6" />
                </div>
                <h3 className="text-2xl font-bold mb-2">{holiday.title}</h3>
                <p className="opacity-90 mb-4">{holiday.description}</p>
                <div className="text-sm opacity-80 font-medium">
                  {isLoading ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    `${getHolidayCount(holiday.holiday).toLocaleString()} movies`
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="bg-[#faf8f6] rounded-3xl shadow-xl p-8 border border-romcom-pink/20">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Seasonal Stats</h3>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader className="w-8 h-8 text-romcom-red animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {seasonCollections.map((s) => (
                <div key={s.season} className="text-center">
                  <div className="text-4xl font-bold text-romcom-red mb-2">
                    {seasonStats[s.season].toLocaleString()}
                  </div>
                  <div className="text-gray-600 capitalize">{s.season} Romcoms</div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SeasonsPage;